(() => {
  const QUALIFYING_HANDLES = new Set([
    'clean-coastal-inflatable-beach-chairs',
    'clean-coastal-beach-towel'
  ]);
  const BLANKET_HANDLE = 'clean-coastal-beach-blanket';

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const routesRoot = () => window.Shopify?.routes?.root || '/';
  const route = (path) => routesRoot() + path.replace(/^\/+/, '');

  const formatMoney = (cents, currency = 'USD') =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency
    }).format(Number(cents || 0) / 100);

  class CCCartBlanketUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ccInitialized === 'true') return;
      this.dataset.ccInitialized = 'true';

      try {
        const data = JSON.parse(this.querySelector('[data-cc-upsell-variants]').textContent);
        this.variants = Array.isArray(data.variants) ? data.variants : [];
      } catch (error) {
        this.variants = [];
      }

      this.discountCode = this.dataset.discountCode || '';
      this.button = this.querySelector('[data-cc-upsell-add]');
      this.status = this.querySelector('[data-cc-upsell-status]');
      this.swatches = [...this.querySelectorAll('[data-cc-upsell-swatch]')];

      this.button?.addEventListener('click', () => this.addBlanket());
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          const variant = this.variants.find(
            (candidate) => String(candidate.id) === String(swatch.dataset.ccUpsellSwatch)
          );
          if (variant?.available) this.renderOffer(variant, this.currency);
        });
      });

      this.refresh();
    }

    async getCart() {
      const response = await fetch(route('cart.js'), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to load your cart.');
      return response.json();
    }

    getAppliedCodes(cart) {
      const applications = [...(cart.cart_level_discount_applications || [])];

      (cart.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((allocation) => {
          if (allocation.discount_application) applications.push(allocation.discount_application);
        });
      });

      return [...new Set(
        applications
          .filter((application) => application && application.type === 'discount_code')
          .map((application) => application.title)
          .filter(Boolean)
      )];
    }

    getColor(item) {
      const colorOption = (item.options_with_values || []).find(
        (option) => normalize(option.name) === 'color'
      );
      return colorOption?.value || item.variant_options?.[0] || '';
    }

    getMatchingVariant(color) {
      const available = this.variants.filter((variant) => variant.available);
      return available.find((variant) => normalize(variant.color) === normalize(color)) || available[0];
    }

    hasCode(codes) {
      return codes.some((code) => normalize(code) === normalize(this.discountCode));
    }

    async updateDiscounts(codes, addCode, render = true) {
      const nextCodes = codes.filter(
        (code) => normalize(code) !== normalize(this.discountCode)
      );
      if (addCode) nextCodes.push(this.discountCode);

      const response = await fetch(route('cart/update.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          discount: nextCodes.join(','),
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname || '/cart'
        })
      });

      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update the discount.');
      if (render) this.renderCart(cart);
      return cart;
    }

    renderCart(cart) {
      if (document.querySelector('cart-items')) {
        window.location.reload();
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && cart.sections) {
        drawer.renderContents(cart);
        return;
      }

      window.location.href = route('cart');
    }

    renderOffer(variant, currency) {
      this.currentVariant = variant;
      this.currency = currency || this.currency || window.Shopify?.currency?.active || 'USD';

      const salePrice = Math.max(0, Number(variant.price || 0) - 800);
      const image = this.querySelector('[data-cc-upsell-image]');

      if (image && variant.image) image.src = variant.image;
      if (image && variant.alt) image.alt = variant.alt;

      const variantText = this.querySelector('[data-cc-upsell-variant]');
      if (variantText) variantText.textContent = String(variant.color || 'Matching color') + ' selected';

      const originalPrice = this.querySelector('[data-cc-upsell-original]');
      if (originalPrice) originalPrice.textContent = formatMoney(variant.price, this.currency);

      const salePriceElement = this.querySelector('[data-cc-upsell-sale]');
      if (salePriceElement) salePriceElement.textContent = formatMoney(salePrice, this.currency);

      this.swatches.forEach((swatch) => {
        const selected = String(swatch.dataset.ccUpsellSwatch) === String(variant.id);
        swatch.classList.toggle('is-selected', selected);
        swatch.setAttribute('aria-pressed', String(selected));
      });

      if (this.button) {
        this.button.textContent = 'Add for ' + formatMoney(salePrice, this.currency);
      }
      this.hidden = false;
    }

    async refresh() {
      if (this.refreshing || !this.variants.length) return;
      this.refreshing = true;

      try {
        const cart = await this.getCart();
        const qualifier = (cart.items || []).find((item) =>
          QUALIFYING_HANDLES.has(item.handle)
        );
        const blanket = (cart.items || []).find((item) => item.handle === BLANKET_HANDLE);
        const codes = this.getAppliedCodes(cart);
        const codeIsActive = this.hasCode(codes);
        const shouldBeActive = Boolean(qualifier && blanket);

        if (codeIsActive !== shouldBeActive) {
          await this.updateDiscounts(codes, shouldBeActive);
          return;
        }

        if (!qualifier || blanket) {
          this.hidden = true;
          return;
        }

        const variant = this.getMatchingVariant(this.getColor(qualifier));
        if (!variant) {
          this.hidden = true;
          return;
        }

        this.renderOffer(variant, cart.currency || window.Shopify?.currency?.active || 'USD');
      } catch (error) {
        this.hidden = true;
      } finally {
        this.refreshing = false;
      }
    }

    async addBlanket() {
      if (!this.currentVariant || this.button?.disabled) return;
      this.button.disabled = true;
      this.button.textContent = 'Adding…';
      if (this.status) this.status.textContent = '';

      try {
        const before = await this.getCart();
        const codes = this.getAppliedCodes(before);

        const addResponse = await fetch(route('cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: String(this.currentVariant.id), quantity: 1 }]
          })
        });
        const added = await addResponse.json();
        if (!addResponse.ok) throw new Error(added.description || 'Unable to add the blanket.');

        await this.updateDiscounts(codes, true);
      } catch (error) {
        if (this.status) {
          this.status.textContent = error.message || 'Something went wrong. Please try again.';
        }
        this.button.disabled = false;
        const salePrice = Math.max(0, Number(this.currentVariant.price || 0) - 800);
        this.button.textContent = 'Add for ' + formatMoney(salePrice, this.currency || 'USD');
      }
    }
  }

  if (!customElements.get('cc-cart-blanket-upsell')) {
    customElements.define('cc-cart-blanket-upsell', CCCartBlanketUpsell);
  }
})();
